import { QUERIES, WithChildren, WITPaginationState } from '@/_metronic/helpers'
import { BaseResponse } from '@/_metronic/helpers/crud-helper/base-list-response'
import { createContext, FC, useContext, useEffect, useMemo, useState } from 'react'
import { useQuery } from 'react-query'
import { FilterReportSalesBudget, initialFilter } from '../../dashboard-forecast/core/_models'
import { getReportSalesBudget } from '../../dashboard-forecast/core/_requests'
import { useQueryRequestMonthly } from '../../dashboard-forecast/core/QueryRequestMonthlyProvider'
import { RevenueActualTarget } from './_models'
import { getRevenueActualTarget } from './_requests'

/**
 * Begin: Initial Values
 */
export const initialQueryResponseActualTarget = {refetch: () => {}, isLoading: false, request: initialFilter}

/**
 * Begin: Response Context
 *
 * It used for context response wrapper, so the response can
 * called everywhere, as long as it wrapped inside the context
 */
type ProductQueryResponseActualTargetContextProps = {
  response?: BaseResponse<RevenueActualTarget[]> | undefined
  refetch: () => void
  isLoading: boolean
  request: FilterReportSalesBudget
}

function createResponseContext<T>(initialState: ProductQueryResponseActualTargetContextProps) {
  return createContext(initialState)
}

const QueryResponseActualTargetContext = createResponseContext<RevenueActualTarget[]>(initialQueryResponseActualTarget)

/**
 * Begin: The Provider
 */
const QueryResponseActualTargetProvider: FC<WithChildren> = ({children}) => {
  const {state} = useQueryRequestMonthly()
  const [request, setRequest] = useState<FilterReportSalesBudget>(state)
  const updatedQuery = useMemo(() => state, [state])

  useEffect(() => {
    if (request !== updatedQuery) {
      setRequest(updatedQuery)
    }
  }, [updatedQuery])

  const {
    isFetching,
    refetch,
    data: response,
  } = useQuery(
    `${QUERIES.REPORT_ACTUAL_TARGET}-${JSON.stringify(request)}`,
    () => {
      return getRevenueActualTarget({year: Number(request.year)})
    },
    {cacheTime: 0, keepPreviousData: true, refetchOnWindowFocus: false}
  )

  return (
    <QueryResponseActualTargetContext.Provider value={{isLoading: isFetching, refetch, response, request}}>
      {children}
    </QueryResponseActualTargetContext.Provider>
  )
}

const useQueryResponseActualTarget = () => useContext(QueryResponseActualTargetContext)

const useQueryResponseActualTargetData = () => {
  const {response} = useQueryResponseActualTarget()
  if (!response) {
    return null
  }

  return response?.response?.data || null
}

const useQueryResponseActualTargetPagination = () => {
  const defaultPaginationState: WITPaginationState = {
    page: 1,
    pageSize: 10,
    totalCount: 1,
  }

  const {response} = useQueryResponseActualTarget()
  if (!response || !response.response) {
    return defaultPaginationState
  }
  const res = response.response
  return {
    page: res.current_page,
    pageSize: res.limit,
    totalCount: res.total_data,
  } as WITPaginationState
}

const useQueryResponseActualTargetLoading = (): boolean => {
  const {isLoading} = useQueryResponseActualTarget()
  return isLoading
}

export {
  QueryResponseActualTargetProvider,
  useQueryResponseActualTarget,
  useQueryResponseActualTargetData,
  useQueryResponseActualTargetLoading,
  useQueryResponseActualTargetPagination
}

